import {
	Dispatch,
	FC,
	ReactElement,
	ReactNode,
	SetStateAction,
	useState,
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Stack, Typography } from '@mui/material';

import { EmailField, SuccessStep } from '@mopla/constants';
import { getSubmitDisabled, usePersistentCountdownGuard } from '@mopla/utils';

import {
	EmailResentSuccessModal,
	MplButton,
	MplErrorModal,
	MplInputV2,
	UserEmailWithEdit,
} from '../../';
import { FormTitle } from '../../layouts/WelcomeScreen';
import { VerificationAnim } from '../../molecules/Animations';

import { Footer, Root } from './ForgotPasswordForm.styles';
import ForgotPasswordValidationSchema from './ForgotPasswordValidation';

interface IFormInput {
	[EmailField.email]: string;
}

interface ForgotPasswordProps {
	onSubmit: (values: IFormInput) => Promise<unknown>;
	successStep: SuccessStep | null;
	error: boolean;
	closeError: VoidFunction;
	setForgotPasswordEmail: Dispatch<SetStateAction<string>>;
	forgotPasswordEmail: string;
	render: (p: { content: ReactNode; submit: ReactNode }) => ReactElement;
}

export const ForgotPasswordForm: FC<ForgotPasswordProps> = ({
	onSubmit,
	successStep,
	error,
	closeError,
	forgotPasswordEmail,
	setForgotPasswordEmail,
	render,
}) => {
	const { t } = useTranslation(['forgotPassword', 'validation']);
	const emailForSignIn = localStorage.getItem('emailForSignIn');
	const [showResentSuccess, setShowResentSuccess] = useState(false);

	const { countdownGuardValue, reinitCountdownGuard } =
		usePersistentCountdownGuard('emailSendTime');

	const methods = useForm<IFormInput>({
		defaultValues: {
			[EmailField.email]: emailForSignIn || '',
		},
		resolver: yupResolver(ForgotPasswordValidationSchema),
		mode: 'onTouched',
	});
	const { formState, handleSubmit, watch } = methods;
	const { isSubmitting } = formState;
	const emailValue = watch('email');

	const handleFormSubmit = async (data: IFormInput) => {
		setForgotPasswordEmail(data[EmailField.email]);
		await onSubmit(data);

		reinitCountdownGuard();
	};

	const handleResentFormSubmit = async (data: IFormInput) => {
		await handleFormSubmit(data);

		setShowResentSuccess(true);
	};

	if (successStep === SuccessStep.forgotPasswordEmailSent) {
		return render({
			content: (
				<Stack className="content" data-testid="forgot-password-success-screen">
					<EmailResentSuccessModal
						open={showResentSuccess}
						onClose={() => setShowResentSuccess(false)}
						email={emailValue}
					/>
					<Box mb={7}>
						<VerificationAnim />
					</Box>

					<FormTitle>
						{t('success_screen.title', { email: forgotPasswordEmail })}
					</FormTitle>

					<Stack gap={3}>
						<UserEmailWithEdit
							sx={{ alignSelf: 'center' }}
							email={forgotPasswordEmail}
						/>
						<Typography
							variant="body2"
							color="solutions.600"
							textAlign="center"
						>
							{t('success_screen.description')}
						</Typography>
					</Stack>
				</Stack>
			),
			submit: (
				<Footer>
					<MplButton
						fullWidth
						variant="outlined"
						color="primaryButtonV2"
						disabled={!!countdownGuardValue || isSubmitting}
						showLoader={isSubmitting}
						onClick={handleSubmit(handleResentFormSubmit)}
					>
						{t('button.send_again', {
							context: countdownGuardValue ? 'wait' : undefined,
							time: countdownGuardValue,
						})}
					</MplButton>
				</Footer>
			),
		});
	}

	return render({
		content: (
			<Stack className="content">
				<FormTitle>{t('text.title')}</FormTitle>
				<FormProvider {...methods}>
					<form
						id="forgotPasswordForm"
						onSubmit={handleSubmit(handleFormSubmit)}
					>
						<Root>
							<Controller
								name="email"
								render={({ field }) => (
									<MplInputV2
										{...field}
										variant="outlined"
										onChange={(e) => {
											e.target.value = e.target.value.trim();
											field.onChange(e);
										}}
										label={t('label.email')}
										helperText={
											Boolean(formState.errors.email?.message) &&
											t(`validation:${formState.errors.email?.message}`)
										}
										error={Boolean(formState.errors.email?.message)}
										inputProps={{
											'data-testid': 'forgot-password-email-textField',
										}}
										type="email"
										clearable
										autoComplete="username email"
									/>
								)}
							/>
						</Root>
					</form>
				</FormProvider>
				<MplErrorModal
					testId="ForgotPasswordForm-MplErrorModal"
					open={error}
					buttons={
						<MplButton fullWidth onClick={closeError}>
							{t('errors:button.close')}
						</MplButton>
					}
				/>
			</Stack>
		),
		submit: (
			<MplButton
				fullWidth
				color="primaryButtonV2"
				data-testid="forgot-password-submit-button"
				disabled={!!countdownGuardValue || getSubmitDisabled(methods)}
				type="submit"
				form="forgotPasswordForm"
				showLoader={isSubmitting}
			>
				{t('button.send_email', {
					context: countdownGuardValue ? 'wait' : undefined,
					time: countdownGuardValue,
				})}
			</MplButton>
		),
	});
};
